import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import toast, { Toaster } from 'react-hot-toast';
import { getMyProspects, updateProspect, createProspect } from "../../interfaces/prospect";
import { getCrmStages } from "../../interfaces/linkedinProspecting";
import { LINKEDIN_STAGE_LABEL, LinkedinStage, crmSuggestion } from "../../lib/linkedinCrm";
import { BsLinkedin } from "react-icons/bs";
import EditProspectModal from "../../components/modals/EditProspectModal";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Select } from "../../components/ui/Select";
import { ViewColumnsIcon } from "@heroicons/react/24/solid";
import { PageHeader } from "../../components/ui/PageHeader";

const notify = (msg: string, type: string) => toast(msg);

// Small popover info button used in column headers
const InfoButton: React.FC<{ info: string }> = ({ info }) => {
    const [open, setOpen] = useState(false);
    return (
        <div
            className="relative inline-block"
            onMouseEnter={(e) => {
                e.stopPropagation();
                setOpen(true);
            }}
            onMouseLeave={(e) => {
                e.stopPropagation();
                setOpen(false);
            }}
        >
            <button
                type="button"
                className="p-1.5 text-ink-faint hover:text-ink hover:bg-surface-sunken rounded-sm transition-colors duration-150"
            >
                <svg
                    className="w-4 h-4 stroke-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 16v-4m0-4h.01"
                    />
                </svg>
            </button>
            {open && (
                <div className="absolute z-20 left-1/2 -translate-x-1/2 mt-2 w-56 rounded-2xl border border-border bg-surface shadow-float p-3 font-sans text-xs text-ink-muted">
                    {info}
                </div>
            )}
        </div>
    );
};

// Quality circle component that shows completion percentage for contact fields
const QualityCircle: React.FC<{ lead: Lead }> = ({ lead }) => {
    const fields = [
        { value: lead.phone, weight: 25 },
        { value: lead.email, weight: 25 },
        { value: lead.website, weight: 25 },
        { value: lead.linkedinUrl, weight: 25 }
    ];

    const completedFields = fields.filter(field => field.value && field.value.trim() !== '');
    const quality = completedFields.reduce((sum, field) => sum + field.weight, 0);



    return (
        <div className="relative group">
            <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-warning text-[10px]`}
                title={`Qualité du contact : ${quality} %`}
            >
                {quality <= 50 && 'Incomplet'}
            </div>
            {/* Tooltip on hover */}
            {quality <= 50 && (
            <div className="absolute top-8 right-0 z-20 rounded-2xl border border-border bg-surface shadow-float p-2 font-sans text-xs text-ink-muted whitespace-nowrap hidden group-hover:block">
                <div className="space-y-1">
                    <div className={`flex items-center gap-2 ${lead.email ? 'text-success' : 'text-ink-faint'}`}>
                        <span>{lead.email ? '✓' : 'x'}</span>
                        <span>Email</span>
                    </div>
                    <div className={`flex items-center gap-2 ${lead.phone ? 'text-success' : 'text-ink-faint'}`}>
                        <span>{lead.phone ? '✓' : 'x'}</span>
                        <span>Téléphone</span>
                    </div>
                    <div className={`flex items-center gap-2 ${lead.website ? 'text-success' : 'text-ink-faint'}`}>
                        <span>{lead.website ? '✓' : 'x'}</span>
                        <span>Site web</span>
                    </div>
                    <div className={`flex items-center gap-2 ${lead.linkedinUrl ? 'text-success' : 'text-ink-faint'}`}>
                        <span>{lead.linkedinUrl ? '✓' : 'x'}</span>
                        <span>LinkedIn</span>
                    </div>
                </div>
            </div>
            )}
        </div>
    );
};

interface Lead {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    company: string;
    position: string;
    source: string;
    dealSize: string;
    value: number;
    avatar: string;
    nextActionAt?: string | null;
    kanbanIndex?: number | null;
    tags?: string[];
    email?: string;
    phone?: string;
    website?: string;
    linkedinUrl?: string;
    updatedAt?: Date;
    logs?: Array<{
        type: string;
        subject?: string;
        message?: string;
        date?: string;
        meta?: any;
    }>;
}

interface DraggedItem {
    lead: Lead;
    sourceColumn: string;
}

interface ColumnInfo {
    title: string;
    tone: "neutral" | "info" | "warning" | "success";
}

interface LeadsData {
    [key: string]: Lead[];
}

interface SourceColors {
    [key: string]: string;
}

interface DealSizeColors {
    [key: string]: string;
}

// Display labels only — the stored value stays Small / Medium / Large
const DEAL_SIZE_LABELS: Record<string, string> = {
    Small: "Petite",
    Medium: "Moyenne",
    Large: "Grande",
};

type ColumnId = "no-status" | "lead" | "qualified" | "follow-up" | "closed";
type ViewType = "kanban" | "list";
// NotificationType unused

const Kanban: React.FC = () => {
    const [selectedView, setSelectedView] = useState<ViewType>("kanban");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
    const [dropIndicator, setDropIndicator] = useState<{
        columnId: string;
        index: number;
    } | null>(null);
    const dragCounter = useRef<number>(0);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingProspectId, setEditingProspectId] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const [leads, setLeads] = useState<LeadsData>({
        "no-status": [],
        lead: [],
        qualified: [],
        "follow-up": [],
        closed: [],
    });
    const [allTags, setAllTags] = useState<string[]>([]);
    const [tagFilter, setTagFilter] = useState<string | null>(null);
    const [recentUpdatesOnly, setRecentUpdatesOnly] = useState<boolean>(false);
    /** CRM prospect id → its stage on the LinkedIn page (matched there on the profile URL). */
    const [linkedinStages, setLinkedinStages] = useState<Record<string, { stage: LinkedinStage; followUpAt: string | null }>>({});

    // Fetch prospects from backend on mount
    useEffect(() => {

        const storedTagFilter = localStorage.getItem("kanban:tag-filter");
        if (storedTagFilter) setTagFilter(storedTagFilter);

        const storedRecentUpdatesOnly = localStorage.getItem("kanban:recent-updates-only");
        if (storedRecentUpdatesOnly === "true") setRecentUpdatesOnly(true);

        const fetchProspects = async () => {
            setIsLoading(true);
            try {
                const data = await getMyProspects();
                if (!data || !data.data) return;
                // Map backend prospects to Kanban columns by status
                const columns: LeadsData = {
                    "no-status": [],
                    lead: [],
                    qualified: [],
                    "follow-up": [],
                    closed: [],
                };
                // Map all, we'll sort inside each column deterministically
                const collectedTags: string[] = [];
                data.data.forEach((prospect: any) => {
                    // accumulate tags locally
                    if (Array.isArray(prospect.tags)) {
                        prospect.tags.forEach((t: string) => {
                            if (t) collectedTags.push(t);
                        });
                    }
                    const status = prospect.status || "new";
                    const columnId =
                        status === "new"
                            ? "no-status"
                            : status === "contacted"
                            ? "lead"
                            : status === "qualified"
                            ? "qualified"
                            : status === "responded" || status === "proposal"
                            ? "follow-up"
                            : status === "won" || status === "lost"
                            ? "closed"
                            : "no-status";
                    columns[columnId].push({
                        id: String(prospect._id),
                        name:
                            prospect.name ||
                            `${prospect.firstName || ""} ${
                                prospect.lastName || ""
                            }`.trim(),
                        firstName: prospect.firstName || "",
                        lastName: prospect.lastName || "",
                        company: prospect.company || "",
                        position: prospect.title || "",
                        source: prospect.source || "",
                        dealSize: prospect.dealSize || "Small",
                        value: prospect.value || 0,
                        avatar:
                            (prospect.firstName && prospect.lastName
                                ? `${prospect.firstName[0]}${prospect.lastName[0]}`
                                : prospect.name
                                ? prospect.name
                                      .split(" ")
                                      .map((n: string) => n[0])
                                      .join("")
                                : "NA"
                            ).toUpperCase(),
                        nextActionAt: prospect.nextActionAt || null,
                        tags: Array.isArray(prospect.tags) ? prospect.tags : [],
                        kanbanIndex: typeof prospect.kanbanIndex === "number" ? prospect.kanbanIndex : null,
                        email: prospect.email || "",
                        phone: prospect.phone || "",
                        website: prospect.website || "",
                        linkedinUrl: prospect.linkedinUrl || "",
                        updatedAt: prospect.updatedAt ? new Date(prospect.updatedAt) : null,
                        logs: Array.isArray(prospect.logs) ? prospect.logs : [],
                    });
                });
                // make unique list stable
                setAllTags((prev) => Array.from(new Set([...prev, ...collectedTags, ...prev])));
                // Sort each column: numeric ranks first ascending, nulls last; tie-break by id
                (Object.keys(columns) as Array<keyof typeof columns>).forEach((col) => {
                    columns[col].sort((a, b) => {
                        const ai = typeof a.kanbanIndex === "number" ? a.kanbanIndex : Number.POSITIVE_INFINITY;
                        const bi = typeof b.kanbanIndex === "number" ? b.kanbanIndex : Number.POSITIVE_INFINITY;
                        if (ai !== bi) return ai - bi;
                        return String(a.id).localeCompare(String(b.id));
                    });
                });
                setLeads(columns);
            } catch (e) {
                console.error("Failed to fetch prospects", e);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProspects();
        getCrmStages().then(setLinkedinStages).catch(() => {});
    }, []);

    // Open prospect modal when ?openProspect=<id> is present in URL
    useEffect(() => {
        // Wait until leads are loaded
        if (isLoading) return;

        const param = searchParams.get("openProspect");
        if (!param) return;

        // Find the prospect in any column
        const found = (Object.keys(leads) as Array<keyof typeof leads>).some((col) =>
            leads[col].some((l) => String(l.id) === String(param)),
        );

        // If not found we still open the modal with the id; modal should fetch remote data by id
        if (found || param) {
            setEditingProspectId(String(param));
            setIsEditOpen(true);

            // remove the param so refresh doesn't re-open unexpectedly
            const next = new URLSearchParams(searchParams);
            next.delete("openProspect");
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    // Create tag in-memory and optionally remote-create hook
    const onTagCreate = (tag: string) => {
        if (!tag) return;
        setAllTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
    };

    const [columnsOrder, setColumnsOrder] = useState<ColumnId[]>([
        "no-status",
        "lead",
        "qualified",
        "follow-up",
        "closed",
    ]);

    const columnInfo: Record<ColumnId, ColumnInfo & { info: string;}> = {
        "no-status": {
            title: "Leads",
            tone: "neutral",
            info: "Nouveaux prospects, sans statut pour l'instant."
        },
        lead: {
            title: "Contactés",
            tone: "info",
            info: "Prospects que vous avez contactés.",
        },
        qualified: {
            title: "Negotiation",
            tone: "warning",
            info: "Prospects intéressés, en cours de négociation.",
        },
        "follow-up": {
            title: "En cours",
            tone: "warning",
            info: "Discussions ou propositions en cours.",
        },
        closed: {
            title: "Clôturés",
            tone: "success",
            info: "Affaires gagnées ou perdues.",
        },
    };


    // API functions
    const saveLeadMove = async (
        leadId: string,
        fromColumn: string,
        toColumn: string,
        newRank: number,
    ): Promise<void> => {
        try {
            // Map columnId -> backend status
            const toStatus =
                toColumn === "no-status"
                    ? "new"
                    : toColumn === "lead"
                    ? "contacted"
                    : toColumn === "qualified"
                    ? "qualified"
                    : toColumn === "follow-up"
                    ? "responded"
                    : "won";
            await updateProspect(leadId, { status: toStatus, kanbanIndex: newRank } as any);
        } catch {
            notify("Impossible de déplacer le prospect", "error");
        }
    };

    // Compute a fractional rank between neighbors to avoid mass updates
    const computeNewRank = (list: Lead[], index: number): number => {
        const findPrev = (): number | null => {
            for (let i = index - 1; i >= 0; i--) {
                const v = list[i]?.kanbanIndex;
                if (typeof v === "number") return v;
            }
            return null;
        };
        const findNext = (): number | null => {
            for (let i = index + 1; i < list.length; i++) {
                const v = list[i]?.kanbanIndex;
                if (typeof v === "number") return v;
            }
            return null;
        };

        const prev = findPrev();
        const next = findNext();
        if (prev !== null && next !== null) {
            if (prev === next) return prev + 0.0001; // avoid duplicates
            return (prev + next) / 2;
        }
        if (prev !== null) return prev + 1;
        if (next !== null) return next - 1;
        return 0; // only item or no numeric ranks yet
    };

    // Optionally implement saveColumnOrder and saveLeadReorder if you want to persist order/columns
    const saveColumnOrder = async (newOrder: ColumnId[]): Promise<void> => {
        // Not implemented: depends on backend support
        notify("Ordre des colonnes enregistré (non conservé)", "success");
    };

    // const saveLeadReorder = async (
    //     columnId: string,
    //     newOrder: Lead[],
    // ): Promise<void> => {
    //     // Not implemented: depends on backend support
    //     notify("Lead order saved (not persisted)", "success");
    // };

    // Lead drag handlers
    const handleLeadDragStart = (
        e: React.DragEvent<HTMLDivElement>,
        lead: Lead,
        sourceColumn: string,
    ): void => {
        setDraggedItem({ lead, sourceColumn });
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", "");
    };

    const handleLeadDragEnd = (e: React.DragEvent<HTMLDivElement>): void => {
        setDraggedItem(null);
        setDropIndicator(null);
        dragCounter.current = 0;
    };

    const getDropIndex = (e: React.DragEvent, columnId: string): number => {
        const column = e.currentTarget as HTMLElement;
        const cards = Array.from(
            column.querySelectorAll('[data-lead-card="true"]'),
        );

        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        cards.forEach((card, index) => {
            const rect = card.getBoundingClientRect();
            const cardCenter = rect.top + rect.height / 2;
            const distance = Math.abs(e.clientY - cardCenter);

            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = e.clientY < cardCenter ? index : index + 1;
            }
        });

        return Math.min(closestIndex, leads[columnId].length);
    };

    const handleLeadDragOver = (
        e: React.DragEvent<HTMLDivElement>,
        columnId: string,
    ): void => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";

        if (!draggedItem) return;

        const dropIndex = getDropIndex(e, columnId);
        setDropIndicator({ columnId, index: dropIndex });
    };

    const handleLeadDrop = async (
        e: React.DragEvent<HTMLDivElement>,
        targetColumn: string,
    ): Promise<void> => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedItem || !dropIndicator) return;

        const { lead, sourceColumn } = draggedItem;
        const targetIndex = dropIndicator.index;

        // Don't drop if it's the same position
        if (sourceColumn === targetColumn) {
            const currentIndex = leads[sourceColumn].findIndex(
                (l) => l.id === lead.id,
            );
            if (
                currentIndex === targetIndex ||
                currentIndex === targetIndex - 1
            ) {
                setDropIndicator(null);
                return;
            }
        }

    // Create new leads state
    const newLeads: LeadsData = { ...leads };

        // Remove from source
        newLeads[sourceColumn] = newLeads[sourceColumn].filter(
            (l) => l.id !== lead.id,
        );

        // Add to target at specific index
        newLeads[targetColumn].splice(targetIndex, 0, lead);

        // Compute a fractional rank using neighbors and set it locally on the moved card
        const newRank = computeNewRank(newLeads[targetColumn], targetIndex);
        newLeads[targetColumn][targetIndex] = {
            ...newLeads[targetColumn][targetIndex],
            kanbanIndex: newRank,
        };

        setLeads(newLeads);
        setDropIndicator(null);

        // Save to API (only the moved card)
        await saveLeadMove(String(lead.id), sourceColumn, targetColumn, newRank);
    // Optionally, refetch prospects to sync with backend
    // const data = await getMyProspects();
    // ...update state if needed

        setDraggedItem(null);
    };

    // Column drag handlers
    const handleColumnDragStart = (
        e: React.DragEvent<HTMLDivElement>,
        columnId: string,
    ): void => {
        setDraggedColumn(columnId);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", "");
    };

    const handleColumnDragEnd = (e: React.DragEvent<HTMLDivElement>): void => {
        setDraggedColumn(null);
    };

    const handleColumnDrop = async (
        e: React.DragEvent<HTMLDivElement>,
        targetColumnId: string,
    ): Promise<void> => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedColumn || draggedColumn === targetColumnId) return;

        const currentIndex = columnsOrder.indexOf(draggedColumn as ColumnId);
        const targetIndex = columnsOrder.indexOf(targetColumnId as ColumnId);

        const newOrder = [...columnsOrder];
        newOrder.splice(currentIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn as ColumnId);

        setColumnsOrder(newOrder);
        await saveColumnOrder(newOrder);
        setDraggedColumn(null);
    };

    // Source / deal size are arbitrary categorical tags, not state — kept neutral per design system
    // (success/warning/danger/info are reserved for real status communication only).
    const getSourceColor = (source: any): string => {
        const colors: SourceColors = {
            LinkedIn: "bg-surface-sunken text-ink-muted border-border",
            Outbound: "bg-surface-sunken text-ink-muted border-border",
            Referral: "bg-surface-sunken text-ink-muted border-border",
            Website: "bg-surface-sunken text-ink-muted border-border",
        };
        return colors[source] || "bg-surface-sunken text-ink-muted border-border";
    };

    const getDealSizeColor = (size: any): string => {
        const colors: DealSizeColors = {
            Small: "bg-surface-sunken text-ink-muted border-border",
            Medium: "bg-surface-sunken text-ink-muted border-border",
            Large: "bg-surface-sunken text-ink-muted border-border",
        };
        return colors[size] || "bg-surface-sunken text-ink-muted border-border";
    };

    // Initials-avatar fallback per DESIGN_SYSTEM.md §2.8 (bg-surface-sunken text-ink-muted)
    const getAvatarColor = (name: string): string => {
        return "bg-surface-sunken";
    };

    const formatNextAction = (iso?: string | null): string => {
        if (!iso) return "";
        const d = new Date(iso);
        if (isNaN(d.getTime())) return "";
        // Short date like Aug 27 or 27/08 depending on locale
        try {
            return d.toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "short",
            });
        } catch {
            return d.toISOString().slice(0, 10);
        }
    };

    // Column <-> stored status, as the drag and drop maps them.
    const COLUMN_STATUS: Record<ColumnId, string> = {
        "no-status": "new",
        lead: "contacted",
        qualified: "qualified",
        "follow-up": "responded",
        closed: "won",
    };

    /** The LinkedIn shortcut: moves a card to the top of the column its LinkedIn stage calls for. */
    const moveToStatus = async (lead: Lead, fromCol: ColumnId, status: string) => {
        const toCol = (Object.keys(COLUMN_STATUS) as ColumnId[]).find((c) => COLUMN_STATUS[c] === status);
        if (!toCol || toCol === fromCol) return;
        const first = leads[toCol][0];
        const kanbanIndex = typeof first?.kanbanIndex === "number" ? first.kanbanIndex - 1 : 0;
        setLeads((prev) => ({
            ...prev,
            [fromCol]: prev[fromCol].filter((l) => l.id !== lead.id),
            [toCol]: [{ ...lead, kanbanIndex }, ...prev[toCol]],
        }));
        try {
            await updateProspect(lead.id, { status, kanbanIndex });
            notify(`Déplacé dans « ${columnInfo[toCol].title} »`, "success");
        } catch {
            notify("Impossible de déplacer le prospect", "error");
        }
    };

    // Check if lead needs outreach (no email/LinkedIn sent in last 10 days)
    const needsOutreach = (lead: Lead): boolean => {
        if (!lead.logs || lead.logs.length === 0) return false;
        if (lead.logs.length >= 2) return false;

        const outreachTypes = ["send-email", "send-linkedin"];
        const outreachLogs = lead.logs
            .filter(log => outreachTypes.includes(log.type))
            .map(log => log.date)
            .filter(date => date && !isNaN(new Date(date).getTime()))
            .map(date => new Date(date!))
            .sort((a, b) => b.getTime() - a.getTime()); // Most recent first

        if (outreachLogs.length === 0) return true;

        const lastOutreach = outreachLogs[0];
        const tenDaysAgo = new Date();
        tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);

        return lastOutreach < tenDaysAgo;
    };

    // Check if lead has been updated in the last 7 days
    const hasRecentUpdate = (lead: Lead): boolean => {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        // Check logs
        let recentLog = false;
        if (lead.logs && lead.logs.length > 0) {
            recentLog = lead.logs
                .map(log => log.date)
                .filter(date => date && !isNaN(new Date(date).getTime()))
                .map(date => new Date(date!))
                .some(date => date > sevenDaysAgo);
        }

        // Check updatedAt
        let recentUpdate = false;
        if ((lead as any).updatedAt) {
            const updatedAtDate = new Date((lead as any).updatedAt);
            if (!isNaN(updatedAtDate.getTime()) && updatedAtDate > sevenDaysAgo) {
                recentUpdate = true;
            }
        }

        console.log(lead);
        return recentLog || recentUpdate;
    };

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <Toaster
                position="top-right"
                reverseOrder={false}
            />
            <div className="max-w-7xl mx-auto px-8">
                <PageHeader icon={<ViewColumnsIcon />} title="CRM" subtitle="Vos prospects, de la prise de contact à la signature" />
            </div>
            {/* Navigation Tabs */}
            <div className="bg-surface border-b border-border">
                <div className="max-w-7xl mx-auto px-8 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 rounded-full bg-surface-sunken p-1">
                            <button
                                onClick={() => setSelectedView("kanban")}
                                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors duration-150 ${
                                    selectedView === "kanban"
                                        ? "bg-surface shadow-card text-ink"
                                        : "text-ink-muted hover:text-ink"
                                }`}
                            >
                                Kanban
                            </button>
                            <button
                                onClick={() => setSelectedView("list")}
                                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors duration-150 ${
                                    selectedView === "list"
                                        ? "bg-surface shadow-card text-ink"
                                        : "text-ink-muted hover:text-ink"
                                }`}
                            >
                                Liste
                            </button>
                        </div>

                        <div className="flex items-center space-x-3">
                            <Button
                                variant="accent"
                                size="sm"
                                onClick={async () => {
                                    try {
                                        const created = await createProspect({
                                            name: "Nouveau prospect",
                                            status: "new",
                                        });
                                        console.log(`Created prospect: ${created}`);
                                        if (!created?._id ) throw new Error("Creation failed");
                                        const p = created;
                                        // Compute rank to place at top of no-status
                                        const topRank = (() => {
                                            const first = leads["no-status"][0];
                                            const r = typeof first?.kanbanIndex === "number" ? first.kanbanIndex : null;
                                            if (r === null) return 0; // column empty or no ranks yet
                                            return r - 1;
                                        })();
                                        // Persist initial rank
                                        try { await updateProspect(String(p._id), { kanbanIndex: topRank } as any); } catch {}
                                        const newLead: Lead = {
                                            id: String(p._id),
                                            name: p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim(),
                                            firstName: p.firstName || "",
                                            lastName: p.lastName || "",
                                            company: p.company || "",
                                            position: p.title || "",
                                            source: p.source || "",
                                            dealSize: p.dealSize || "Small",
                                            value: p.value || 0,
                                            avatar:
                                                (p.firstName && p.lastName
                                                    ? `${p.firstName[0]}${p.lastName[0]}`
                                                    : p.name
                                                    ? p.name
                                                          .split(" ")
                                                          .map((n: string) => n[0])
                                                          .join("")
                                                    : "NA"
                                                ).toUpperCase(),
                                            nextActionAt: p.nextActionAt || null,
                                            kanbanIndex: topRank,
                                            tags: Array.isArray(p.tags) ? p.tags : [],
                                            email: p.email || "",
                                            phone: p.phone || "",
                                            website: p.website || "",
                                            linkedinUrl: p.linkedinUrl || "",
                                            logs: Array.isArray(p.logs) ? p.logs : [],
                                        };
                                        setLeads((prev) => ({
                                            ...prev,
                                            "no-status": [newLead, ...prev["no-status"]],
                                        }));
                                        setEditingProspectId(String(p._id));
                                        setIsEditOpen(true);
                                        notify("Prospect créé", "success");
                                    } catch (e: any) {
                                        console.error(`Failed to create prospect: ${e}`);
                                    }
                                }}
                            >
                                <svg
                                    className="w-4 h-4 stroke-2"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                                Prospect
                            </Button>
                            {/* <button className="p-2 text-ink-faint hover:text-ink hover:bg-surface-sunken rounded-sm transition-colors duration-150">
                                <svg
                                    className="w-5 h-5 stroke-2"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z"
                                    />
                                </svg>
                            </button> */}
                        </div>
                    </div>
                </div>
            </div>

            {selectedView === "kanban" ? (
                <div className="mt-2 px-8">
                    {/* Filters */}
                        <div className="flex justify-end items-center space-x-4">
                            <label className="flex items-center space-x-2 text-xs text-ink-muted">
                                <input
                                    type="checkbox"
                                    checked={recentUpdatesOnly}
                                    onChange={(e) => {
                                        setRecentUpdatesOnly(e.target.checked);
                                        localStorage.setItem("kanban:recent-updates-only", e.target.checked.toString());
                                    }}
                                    className="accent-ink rounded-sm border-border"
                                />
                                <span>Récents uniquement</span>
                            </label>
                            <Select
                                value={tagFilter || ""}
                                onChange={(v) => {
                                    setTagFilter(v || null)
                                    localStorage.setItem("kanban:tag-filter", v || "");
                                }}
                                options={[
                                    { value: "", label: "Tous les tags" },
                                    ...allTags.map((t) => ({ value: t, label: t })),
                                ]}
                            />
                        </div>
                    <div className="mt-2 flex space-x-4 overflow-x-auto pb-4">
                        {isLoading && (
                            columnsOrder.map((columnId) => (
                                <div key={`skeleton-${columnId}`} className="flex-shrink-0 w-80">
                                    <div className="bg-surface rounded-2xl border border-border">
                                        <div className="px-4 py-3 border-b border-border rounded-t-2xl">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3 w-full">
                                                    <span className="inline-block h-6 w-24 rounded-full bg-surface-sunken motion-safe:animate-pulse" />
                                                    <span className="inline-block h-4 w-6 rounded-sm bg-surface-sunken motion-safe:animate-pulse" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-3 min-h-[200px]">
                                            {[0, 1, 2].map((i) => (
                                                <div
                                                    key={`sk-${columnId}-${i}`}
                                                    className="bg-surface border border-border rounded-2xl p-3 mb-2"
                                                    style={{ height: "104px" }}
                                                >
                                                    <div className="h-full w-full space-y-2 motion-safe:animate-pulse">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-8 h-8 rounded-full bg-surface-sunken" />
                                                            <div className="flex-1 space-y-2">
                                                                <div className="h-3 w-2/3 bg-surface-sunken rounded-sm" />
                                                                <div className="h-3 w-1/3 bg-surface-sunken rounded-sm" />
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between pt-2">
                                                            <div className="flex gap-2">
                                                                <div className="h-5 w-16 bg-surface-sunken rounded-sm" />
                                                                <div className="h-5 w-14 bg-surface-sunken rounded-sm" />
                                                                <div className="h-5 w-20 bg-surface-sunken rounded-sm" />
                                                            </div>
                                                            <div className="h-3 w-10 bg-surface-sunken rounded-sm" />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        {!isLoading &&
                        columnsOrder.map((columnId) => (
                            <div key={columnId} className="flex-shrink-0 w-80">
                                <div
                                    className={`bg-surface rounded-2xl border border-border transition-all duration-200 ${
                                        draggedColumn === columnId
                                            ? "ring-2 ring-ink"
                                            : ""
                                    }`}
                                >
                                    {/* Column Header */}
                                    <div
                                        className="px-4 py-3 border-b border-border cursor-move rounded-t-2xl hover:bg-surface-sunken transition-colors duration-150"
                                        draggable
                                        onDragStart={(e) =>
                                            handleColumnDragStart(e, columnId)
                                        }
                                        onDragEnd={handleColumnDragEnd}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleColumnDrop(e, columnId);
                                        }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <Badge tone={columnInfo[columnId].tone}>
                                                    {columnInfo[columnId].title}
                                                </Badge>
                                                <span className="text-sm font-medium text-ink-muted">
                                                    {leads[columnId].length}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                <InfoButton info={columnInfo[columnId].info} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Lead Cards Drop Zone */}
                                    <div
                                        className="p-3 min-h-[200px] relative"
                                        onDragOver={(e) =>
                                            handleLeadDragOver(e, columnId)
                                        }
                                        onDrop={(e) => handleLeadDrop(e, columnId)}
                                        onDragLeave={(e) => {
                                            const rect =
                                                e.currentTarget.getBoundingClientRect();
                                            const x = e.clientX;
                                            const y = e.clientY;

                                            if (
                                                x < rect.left ||
                                                x > rect.right ||
                                                y < rect.top ||
                                                y > rect.bottom
                                            ) {
                                                setDropIndicator(null);
                                            }
                                        }}
                                    >
                                        {/* Drop Indicator */}
                                        {dropIndicator?.columnId === columnId && (
                                            <div
                                                className="absolute left-3 right-3 h-0.5 bg-ink rounded-full z-10 transition-all duration-150"
                                                id={`drop-indicator-${columnId}`}
                                                style={{
                                                    top:
                                                        dropIndicator.index === 0
                                                            ? "12px"
                                                            : `${
                                                                  12 +
                                                                  dropIndicator.index *
                                                                      (104 + 8) -
                                                                  4
                                                              }px`,
                                                }}
                                            />
                                        )}

                                        <div className="space-y-2">
                                            {leads[columnId]
                                                .filter((lead) => {
                                                    // Apply tag filter
                                                    if (tagFilter) {
                                                        const tags = Array.isArray(lead.tags) ? lead.tags : [];
                                                        if (!tags.includes(tagFilter)) return false;
                                                    }
                                                    
                                                    // Apply recent updates filter
                                                    if (recentUpdatesOnly) {
                                                        return hasRecentUpdate(lead);
                                                    }
                                                    
                                                    return true;
                                                })
                                                .map(
                                                (lead: any, index: number) => (
                                                    <div
                                                        key={lead.id}
                                                        data-lead-card="true"
                                                        draggable
                                                        onDragStart={(e) => {
                                                            e.stopPropagation();
                                                            handleLeadDragStart(
                                                                e,
                                                                lead,
                                                                columnId,
                                                            );
                                                        }}
                                                        onDragEnd={(e) => {
                                                            e.stopPropagation();
                                                            handleLeadDragEnd(e);
                                                        }}
                                                        className={`bg-surface border border-border rounded-2xl p-3 hover:border-border-strong transition-all duration-200 cursor-pointer group relative ${
                                                            draggedItem?.lead.id ===
                                                            lead.id
                                                                ? "opacity-50 transform scale-105"
                                                                : ""
                                                        }`}
                                                        style={{ height: "104px" }}
                                                        onClick={() => {
                                                            setEditingProspectId(String(lead.id));
                                                            setIsEditOpen(true);
                                                        }}
                                                    >
                                                        {/* Quality Circle - positioned absolutely in top right */}
                                                        <div className="absolute top-1 right-5 pointer-events-none">
                                                            <QualityCircle lead={lead} />
                                                        </div>
                                                        <div className="flex items-start space-x-3 pointer-events-none h-full pr-8">{/* Added pr-8 to prevent overlap with quality circle */}
                                                            <div
                                                                className={`w-8 h-8 rounded-full flex items-center justify-center text-ink-muted text-xs font-medium flex-shrink-0 ${getAvatarColor(
                                                                    lead.name,
                                                                )}`}
                                                            >
                                                                {lead.avatar}
                                                            </div>
                                                            <div className="flex-1 min-w-0 overflow-hidden">
                                                                <h3 className="text-sm font-medium text-ink truncate leading-tight">
                                                                    {lead.firstName} {lead.lastName}
                                                                </h3>
                                                                <p className="text-xs text-ink-muted truncate mt-0.5">
                                                                    {lead.position}{" "}
                                                                    chez{" "}
                                                                    {lead.company}
                                                                </p>

                                                                <div className="mt-2 flex items-center justify-between">
                                                                    <div className="flex items-center flex-wrap gap-1.5">
                                                                        {lead.tags &&
                                                                            Array.isArray(lead.tags) &&
                                                                            lead.tags.length > 0 && (
                                                                                <span
                                                                                    className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${getSourceColor(
                                                                                        lead.source,
                                                                                    )}`}
                                                                                >
                                                                                    {lead.tags[0]}
                                                                                </span>
                                                                            )}
                                                                        {linkedinStages[lead.id] && (() => {
                                                                            const { stage } = linkedinStages[lead.id];
                                                                            const move = crmSuggestion(stage, COLUMN_STATUS[columnId as ColumnId]);
                                                                            const label = LINKEDIN_STAGE_LABEL[stage];
                                                                            return move ? (
                                                                                <button
                                                                                    type="button"
                                                                                    draggable={false}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        moveToStatus(lead, columnId as ColumnId, move.status);
                                                                                    }}
                                                                                    title={`LinkedIn : ${label} — déplacer dans « ${move.label} »`}
                                                                                    className="pointer-events-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium border border-border-strong bg-surface text-ink hover:bg-surface-sunken"
                                                                                >
                                                                                    <BsLinkedin className="w-3 h-3" />
                                                                                    {label} → {move.label}
                                                                                </button>
                                                                            ) : (
                                                                                <span
                                                                                    title={`LinkedIn : ${label}`}
                                                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium border bg-surface-sunken text-ink-muted border-border"
                                                                                >
                                                                                    <BsLinkedin className="w-3 h-3" />
                                                                                    {label}
                                                                                </span>
                                                                            );
                                                                        })()}
                                                                        {lead.nextActionAt && !needsOutreach(lead) && (
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border bg-warning-subtle text-warning border-warning/20">
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    viewBox="0 0 24 24"
                                                                                    fill="currentColor"
                                                                                    className="w-3.5 h-3.5 mr-1"
                                                                                >
                                                                                    <path d="M6.75 3a.75.75 0 01.75.75V5h9V3.75a.75.75 0 011.5 0V5h.75A2.25 2.25 0 0121 7.25v10.5A2.25 2.25 0 0118.75 20H5.25A2.25 2.25 0 013 17.75V7.25A2.25 2.25 0 015.25 5H6V3.75A.75.75 0 016.75 3zm12 6h-13.5a.75.75 0 00-.75.75v8c0 .414.336.75.75.75h13.5a.75.75 0 00.75-.75v-8a.75.75 0 00-.75-.75z" />
                                                                                </svg>
                                                                                {formatNextAction(lead.nextActionAt)}
                                                                            </span>
                                                                        )}
                                                                        {needsOutreach(lead) && (
                                                                            <span
                                                                                className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border bg-danger-subtle text-danger border-danger/20"
                                                                                title="Aucun email ni message LinkedIn envoyé ces 10 derniers jours"
                                                                            >
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    viewBox="0 0 24 24"
                                                                                    fill="currentColor"
                                                                                    className="w-3.5 h-3.5 mr-1"
                                                                                >
                                                                                    <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                                                                                </svg>
                                                                                Première relance
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="max-w-7xl mx-auto px-8 py-8">
                    <div className="bg-surface border border-border rounded-2xl overflow-hidden">
                        <table className="min-w-full divide-y divide-border">
                            <thead className="bg-surface-sunken">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wide">Nom</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wide">Entreprise</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wide">Poste</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wide">Source</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wide">Taille</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-ink-faint uppercase tracking-wide">Valeur</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wide">Colonne</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wide">Prochaine action</th>
                                </tr>
                            </thead>
                            <tbody className="bg-surface divide-y divide-border">
                                {isLoading ? (
                                    Array.from({ length: 8 }).map((_, i) => (
                                        <tr key={`sk-row-${i}`}>
                                            <td className="px-4 py-3">
                                                <div className="h-3 w-32 bg-surface-sunken rounded-sm motion-safe:animate-pulse" />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="h-3 w-28 bg-surface-sunken rounded-sm motion-safe:animate-pulse" />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="h-3 w-24 bg-surface-sunken rounded-sm motion-safe:animate-pulse" />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="h-5 w-16 bg-surface-sunken rounded-sm motion-safe:animate-pulse" />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="h-5 w-14 bg-surface-sunken rounded-sm motion-safe:animate-pulse" />
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="ml-auto h-3 w-10 bg-surface-sunken rounded-sm motion-safe:animate-pulse" />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="h-3 w-20 bg-surface-sunken rounded-sm motion-safe:animate-pulse" />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="h-3 w-16 bg-surface-sunken rounded-sm motion-safe:animate-pulse" />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    (Object.keys(leads) as Array<keyof typeof leads>).flatMap((colId) =>
                                        leads[colId]
                                            .filter((lead) => {
                                                // Apply tag filter
                                                if (tagFilter) {
                                                    const tags = Array.isArray(lead.tags) ? lead.tags : [];
                                                    if (!tags.includes(tagFilter)) return false;
                                                }

                                                // Apply recent updates filter
                                                if (recentUpdatesOnly) {
                                                    return hasRecentUpdate(lead);
                                                }

                                                return true;
                                            })
                                            .map((lead) => (
                                            <tr
                                                key={`${String(colId)}-${lead.id}`}
                                                className="hover:bg-surface-sunken/60 cursor-pointer transition-colors duration-150"
                                                onClick={() => {
                                                    setEditingProspectId(String(lead.id));
                                                    setIsEditOpen(true);
                                                }}
                                            >
                                                <td className="px-4 py-2 text-sm text-ink">{lead.name}</td>
                                                <td className="px-4 py-2 text-sm text-ink-muted">{lead.company}</td>
                                                <td className="px-4 py-2 text-sm text-ink-muted">{lead.position}</td>
                                                <td className="px-4 py-2 text-sm"><span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${getSourceColor(lead.source)}`}>{lead.source || ""}</span></td>
                                                <td className="px-4 py-2 text-sm"><span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${getDealSizeColor(lead.dealSize)}`}>{DEAL_SIZE_LABELS[lead.dealSize] || lead.dealSize || DEAL_SIZE_LABELS.Small}</span></td>
                                                <td className="px-4 py-2 text-sm text-right text-ink">${lead.value.toLocaleString()}</td>
                                                <td className="px-4 py-2 text-sm text-ink-muted">{columnInfo[(colId as string) as ColumnId]?.title}</td>
                                                <td className="px-4 py-2 text-sm text-ink-muted">{formatNextAction(lead.nextActionAt)}</td>
                                            </tr>
                                        )),
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Edit Prospect Modal */}
            <EditProspectModal
                isOpen={isEditOpen}
                onClose={() => setIsEditOpen(false)}
                prospectId={editingProspectId}
                allTags={allTags}
                onTagCreate={onTagCreate}
                onLinkedinStage={(id, stage, followUpAt) =>
                    setLinkedinStages((prev) => {
                        const next = { ...prev };
                        // The board only shows what happened on LinkedIn.
                        if (stage === "discover") delete next[id];
                        else next[id] = { stage, followUpAt };
                        return next;
                    })
                }
                onSave={async (updated: any) => {
                    if (!editingProspectId) return;
                    if (updated) {
                        // Refresh minimally: move card if status changed and update visible fields
                        setLeads((prev) => {
                            const findColumn = (status: string) =>
                                status === "new"
                                    ? "no-status"
                                    : status === "contacted"
                                    ? "lead"
                                    : status === "qualified"
                                    ? "qualified"
                                    : status === "responded" || status === "proposal"
                                    ? "follow-up"
                                    : "closed";
                            const fromCol = (Object.keys(prev) as string[]).find((c) =>
                                prev[c].some((l) => String(l.id) === String(editingProspectId))
                            ) as ColumnId | undefined;
                            const toCol = findColumn(updated.status) as ColumnId;
                            const newState: LeadsData = { ...prev } as any;
                            if (fromCol) {
                                newState[fromCol] = newState[fromCol].filter(
                                    (l) => String(l.id) !== String(editingProspectId)
                                );
                            }
                            const card: Lead = {
                                id: String(updated._id),
                                name: updated.name || `${updated.firstName || ""} ${updated.lastName || ""}`.trim(),
                                firstName: updated.firstName || "",
                                lastName: updated.lastName || "",
                                company: updated.company || "",
                                position: updated.title || "",
                                source: updated.source || "",
                                dealSize: updated.dealSize || "Small",
                                value: updated.value || 0,
                                avatar:
                                    (updated.firstName && updated.lastName
                                        ? `${updated.firstName[0]}${updated.lastName[0]}`
                                        : updated.name
                                        ? updated.name
                                              .split(" ")
                                              .map((n: string) => n[0])
                                              .join("")
                                        : "NA"
                                    ).toUpperCase(),
                                nextActionAt: updated.nextActionAt || null,
                                tags: Array.isArray(updated.tags) ? updated.tags : [],
                                email: updated.email || "",
                                phone: updated.phone || "",
                                website: updated.website || "",
                                linkedinUrl: updated.linkedinUrl || "",
                                updatedAt: updated.updatedAt || "",
                                logs: Array.isArray(updated.logs) ? updated.logs : [],
                            };
                            // If moved to a new column, compute a rank to insert at top and persist it
                            if (!fromCol || fromCol !== toCol) {
                                const first = newState[toCol][0];
                                const r = typeof first?.kanbanIndex === "number" ? first.kanbanIndex : null;
                                const topRank = r === null ? 0 : r - 1;
                                card.kanbanIndex = topRank;
                                // Persist rank best-effort
                                updateProspect(String(updated._id), { kanbanIndex: topRank } as any).catch(() => {});
                                newState[toCol] = [card, ...newState[toCol]];
                            } else {
                                // Stay in same column: keep existing rank if we can find, else push top
                                const existing = prev[fromCol].find((l) => String(l.id) === String(editingProspectId));
                                card.kanbanIndex = existing?.kanbanIndex ?? null;
                                newState[toCol] = [card, ...newState[toCol]];
                                // Sort the column to respect rank ordering
                                newState[toCol].sort((a, b) => {
                                    const ai = typeof a.kanbanIndex === "number" ? a.kanbanIndex : Number.POSITIVE_INFINITY;
                                    const bi = typeof b.kanbanIndex === "number" ? b.kanbanIndex : Number.POSITIVE_INFINITY;
                                    if (ai !== bi) return ai - bi;
                                    return String(a.id).localeCompare(String(b.id));
                                });
                            }
                            return newState;
                        });
                    }
                    setIsEditOpen(false);
                    setEditingProspectId(null);
                }}
                onDelete={(id: string) => {
                    setLeads((prev) => {
                        const next: LeadsData = { ...prev } as any;
                        (Object.keys(next) as string[]).forEach((col) => {
                            next[col] = next[col].filter((l) => String(l.id) !== String(id));
                        });
                        return next;
                    });
                    setIsEditOpen(false);
                    setEditingProspectId(null);
                    notify("Prospect supprimé", "success");
                }}
            />

            {/* Help Button */}
            {!isEditOpen && (
                <div className="fixed bottom-6 right-6">
                    <button className="w-12 h-12 bg-surface border border-border rounded-full flex items-center justify-center text-ink-muted hover:text-ink hover:border-border-strong shadow-float transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                        <svg
                            className="w-5 h-5 stroke-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                            />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default Kanban;
